import { Alert, Box, Button, Checkbox, FormControlLabel } from '@mui/material'
import { useCallback, useContext, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NotificationContext } from '../../providers'
import {
  useGetShopProfileQuery,
  useUpdateShopProfileMutation
} from '../../redux/api'
import {
  useAuthorizeShopForCarfaxMutation,
  useAuthorizeShopForQuickVinMutation
} from '../../redux/api/shopMatrixApi'
import { ShopEntry } from '../../sdk'

export const CarfaxButton = ({
  shopEntry
}: {
  shopEntry: ShopEntry | undefined
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { setNotice } = useContext(NotificationContext)

  const [isSubmittingCarfax, setIsSubmittingCarfax] = useState<boolean>(false)
  const [updateShop] = useUpdateShopProfileMutation()
  const [authorizeShopForCarfax] = useAuthorizeShopForCarfaxMutation()
  const [authorizeShopForQuickVin] = useAuthorizeShopForQuickVinMutation()
  const { refetch } = useGetShopProfileQuery({
    authorization: accessToken,
    id: shopId
  })

  const handleChangeEnableCarfax = useCallback(
    async (checked: boolean) => {
      setIsSubmittingCarfax(true)
      if (checked) {
        await authorizeShopForCarfax({
          authorization: accessToken,
          body: shopId
        })
      } else {
        await updateShop({
          authorization: accessToken,
          shopEntry: {
            ...shopEntry!,
            info: {
              ...shopEntry!.info!,
              enableCarfax: false
            }
          }
        })
      }
      refetch()
      setIsSubmittingCarfax(false)

      setNotice('Settings successfully updated')
    },
    [
      accessToken,
      authorizeShopForCarfax,
      refetch,
      setNotice,
      shopEntry,
      shopId,
      updateShop
    ]
  )

  const handleChangeEnableQuickVin = useCallback(
    async (checked: boolean) => {
      setIsSubmittingCarfax(true)
      if (checked) {
        await authorizeShopForQuickVin({
          authorization: accessToken,
          body: shopId
        })
      }
      setIsSubmittingCarfax(false)
      setNotice('Settings successfully updated')
    },
    [authorizeShopForQuickVin, accessToken, shopId, setNotice]
  )

  if (!shopEntry) {
    return <></>
  }

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            defaultChecked={!!shopEntry?.info?.enableCarfax}
            onChange={(e) => {
              handleChangeEnableCarfax(e.target.checked)
            }}
          />
        }
        label="Enable Upload to CarFax and Service History Lookup"
        disabled={isSubmittingCarfax}
      />
      {shopEntry.info?.enableCarfax && (
        <>
          {shopEntry.info?.carfaxAuthorized ? (
            <Box pb={1}>
              <Alert severity="success">
                Access to CarFax history is granted. It might take up to 3 hours
                for request to be processed.
              </Alert>
            </Box>
          ) : (
            <Button onClick={() => handleChangeEnableCarfax(true)}>
              Request access to CarFax history
            </Button>
          )}
        </>
      )}
      <FormControlLabel
        control={
          <Checkbox
            defaultChecked={!!shopEntry?.info?.quickVinAuthorized}
            onChange={(e) => {
              handleChangeEnableQuickVin(e.target.checked)
            }}
          />
        }
        label="Enable Upload to CarFax and License Plate Lookup (a.k.a. QuickVIN)"
        disabled={isSubmittingCarfax}
      />
      {shopEntry.info?.enableCarfax && (
        <>
          {shopEntry.info?.quickVinAuthorized ? (
            <Box pb={1}>
              <Alert severity="success">
                Access to CarFax QuickVIN is granted. It might take up to 3
                hours for request to be processed.
              </Alert>
            </Box>
          ) : (
            <Button onClick={() => handleChangeEnableQuickVin(true)}>
              Request access to CarFax QuickVIN
            </Button>
          )}
        </>
      )}
    </>
  )
}
